import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import {
  Field,
  FormComponent,
  FormReturnType,
  NaturalFormConfig,
  NaturalFormFlavour,
  NaturalFormStyle,
  Section,
} from "@cotecna/dynamic-view-engine";
import { InspectionState } from "src/app/inspection/model/inspection-state";
import { InspectionFormService } from "src/app/inspection/services/inspection-form-service/inspection-form.service";
import { ADD_DATES_FORM, NATURAL_CONTROLS } from "./add-dates-form";
import { CloneInspectionData } from "./clone-inspection-data";

@Component({
    selector: "im-clone-inspection-dialog",
    templateUrl: "./clone-inspection-dialog.component.html",
    styleUrls: ["./clone-inspection-dialog.component.scss"],
    standalone: false
})
export class CloneInspectionDialogComponent implements OnInit, AfterViewInit {
  formConfig: NaturalFormConfig = <NaturalFormConfig>{
    displayMode: NaturalFormStyle.material,
    flavor: NaturalFormFlavour.informative,
    notifyFieldChange: true,
    returnType: FormReturnType.entity,
  };

  @ViewChild("naturalForm")
  form: FormComponent;

  constructor(
    private dialogRef: MatDialogRef<CloneInspectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: any,
    private inspectionFormService: InspectionFormService,
    private dialog: MatDialog,
  ) {}

  public inspectionName: string;
  public newInspectionName: string;
  public defaultQuantity = 1;
  public numberQuantities: number[] = Array.from(
    { length: 20 },
    (_, i) => i + 1,
  );

  public copyDates: boolean;
  public cloneInspectionPage: boolean = true;
  public cloneButtonDisabled: boolean = true;
  public isNewInspectionState: boolean = false;

  private selectedQuantity: number;
  private copyForm: boolean;
  private copyInspectors: boolean;
  private dates: Date[];

  controls: any[] = NATURAL_CONTROLS;
  sections: Section[] = ADD_DATES_FORM;

  ngOnInit(): void {
    this.inspectionName = this.dialogData.inspection.generalInfo?.externalId;
    this.selectedQuantity = this.defaultQuantity;
    this.copyForm = false;
    this.copyInspectors = false;
    if (this.dialogData.inspection.state === InspectionState.New) {
      this.isNewInspectionState = true;
    }
  }

  ngAfterViewInit(): void {
    if (this.form?.fieldChange)
      this.form.fieldChange.subscribe((field: Field) => {
        this.cloneButtonDisabled = !field.value;
        const dates: string[] = field.value.split(",");
        this.dates = dates.map((date) => new Date(date));
      });
  }

  onChange(newName): void {
    this.newInspectionName = newName;
  }

  public changeQuantity(quantity: number) {
    this.selectedQuantity = quantity;
  }

  public checkCopyDates(value: boolean) {
    this.copyDates = value;
  }

  public checkCopyForms(value: boolean) {
    this.copyForm = value;
  }

  public checkCopyInspectors(value: boolean) {
    this.copyInspectors = value;
  }

  async clone() {
    let inspectionNameIsCorrect = !(await this.inspectionFormService
      .checkIfInspectionExists(this.newInspectionName)
      .toPromise());
    if (inspectionNameIsCorrect) {
      this.dialogRef.close({
        inspectionId: this.dialogData.inspection.id,
        quantity: this.selectedQuantity,
        cloneFormData: this.copyForm,
        cloneInspectors: this.copyInspectors,
        cloneDates: this.copyDates,
        newInspectionName: this.newInspectionName,
        dates: this.dates,
      } as CloneInspectionData);
    } else {
      this.openNameAlreadyExistsDialog();
    }
  }

  private openNameAlreadyExistsDialog() {
    this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: <ConfirmDialogData>{
        icon: "warning",
        okButton: {
          text: "ACCEPT",
          color: "primary",
        },
        cancelButton: {
          text: "",
        },
        title: "Inspection already exists",
        message: `The name ${this.newInspectionName} already exists for another inspection.`,
      },
    });
  }

  async next() {
    let inspectionNameIsCorrect = !(await this.inspectionFormService
      .checkIfInspectionExists(this.newInspectionName)
      .toPromise());
    if (inspectionNameIsCorrect) {
      this.resetValues();
      this.sections = ADD_DATES_FORM;
      this.cloneInspectionPage = false;
    } else {
      this.openNameAlreadyExistsDialog();
    }
  }

  back() {
    this.resetValues();
    this.sections = [];
    this.dates = [];
    this.cloneInspectionPage = true;
    this.cloneButtonDisabled = true;
  }

  private resetValues() {
    ADD_DATES_FORM.forEach((s) => {
      s.fields.forEach((f) => {
        f.value = null;
      });
    });
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
